import classnames from 'classnames';
import { debounce } from 'lodash';
import React, {
    forwardRef,
    HtmlHTMLAttributes,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import IconFont from '@meitu/xdesign-icon-font';
import { useConfig } from '@/configProvider';
import { useLocale } from '@/i18n';
import { useCurrentTeamIdentity } from '@/teamInfo';
import { IdentityType } from '@/types';
import eventBus from '@/utils/eventBus';
import { PriceCalculateResponse } from '../../services';
import { checkConfirmStatus, openSubscribe } from '../../utils';
import './index.less';

// 缓存算价数据
let infoCache: any = null;

interface IProps extends HtmlHTMLAttributes<HTMLDivElement> {
    /**
     * 按钮的唯一标识
     */
    uuid?: string;
    /**
     * 是否免费。c端用户需要消耗美豆，华为体验版内嵌免费，SDK服务免费
     */
    isFree?: boolean;
    disabled?: boolean;
    loading?: boolean;
    /**
     * 按钮文案
     */
    text?: React.ReactNode;
    /**
     * 是否隐藏按钮下边的"试用次数/美豆"文案
     */
    hideDesc?: boolean;
    /**
     * 用于记录该入口美豆充值归属，如果不设置默认跟美豆下拉面板来源一样
     */
    source?: string;
    /**
     * 当前按钮触发订阅透传的second_source
     */
    second_source?: string;
    /**
     * 订阅透传其他参数
     */
    vipParams?: Record<string, any>;
    /**
     * tooltip的内容
     */
    tooltipTitle?: React.ReactNode;
    onErrorMessage?: (msg: string) => void;
    /**
     * 美豆不足回调
     */
    notEnoughMeidouCallback?: () => void;
    onGen?: () => void;
    /**
     * 点击前回调
     * @returns
     */
    onBeforeGen?: () => boolean | Promise<boolean>;
    /**
     * 是否走v2的策略，如果执行v2 则在 onBeforeGen 钩子中判断 useUserPolicy钩子
     */
    useV2?: boolean;
    /**
     * 是否强制校验算价结果 success
     */
    forceCheckSuccess?: boolean;

    /**
     * 隐藏价格
     */
    hidePrice?: boolean;
    /**
     * 隐藏美豆不足提示文案
     */
    hideToast?: boolean;

    /**
     * 美豆不足提示文案自定义
     */
    toastText?: string;

    /**
     * 显示在按钮前面的 icon，与 loading icon 互斥
     */
    prefixIconName?: string;
    isVip?: boolean;

    /**
     * 显示在按钮后面的额外内容
     */
    extra?: React.ReactNode;
}

export interface GenRef {
    onClick: () => void;
}

const GenButton = forwardRef<GenRef, IProps>(
    (
        {
            style,
            className,
            uuid = '',
            isFree = false,
            disabled,
            loading,
            text,
            hideDesc = false,
            source,
            second_source,
            tooltipTitle,
            onGen,
            onErrorMessage,
            notEnoughMeidouCallback,
            onBeforeGen,
            toastText,
            vipParams,
            useV2,
            forceCheckSuccess = false,
            prefixIconName,
            hidePrice = false,
            hideToast = false,
            isVip = false,
            extra,
        },
        ref,
    ) => {
        // ref变量是为了解决在before钩子里刷新算价后，在gen函数里可以马上取到最新的算价值
        const infoRef = useRef<PriceCalculateResponse['data']>(infoCache);
        const [info, setInfo] = useState<PriceCalculateResponse['data']>(infoCache);
        const handleGenRef = useRef<any>();
        const { $t } = useLocale();
        const currentIdentity = useCurrentTeamIdentity();
        const { isIntl } = useConfig();

        text = text || $t((v) => v.meidouInfo.genBtnText);

        const handleToast = () => {
            const ele = document.createElement('div');
            ele.style.zIndex = '99999';
            ele.style.position = 'fixed';
            ele.style.top = '40px';
            ele.style.left = '50%';
            ele.style.transform = 'translateX(-50%)';
            ele.style.backgroundColor = 'var(--background-web-primary, #fff)';
            ele.style.color = 'color: var(--content-btn-primary, #16171a)';
            ele.style.boxShadow = '0 10px 40px 0 rgba(0, 0, 0, 10%), 0 0 2px 0 rgba(0, 0, 0, 8%)';
            ele.style.padding = '8px 15px';
            ele.style.fontSize = '16px';
            ele.style.borderRadius = '5px';
            ele.innerHTML =
                toastText || $t((v) => (isVip ? v.tip_insufficientCredits : v.tip_insufficientCredits_upgrade));

            document.body.appendChild(ele);

            setTimeout(() => {
                document.body.removeChild(ele);
            }, 5000);
        };

        const handleSuccessCheckFailed = () => {
            // success=false， type=2 引导充值
            if (!infoRef.current?.success && infoRef.current?.type === 2) {
                if (notEnoughMeidouCallback) {
                    notEnoughMeidouCallback();
                    return true;
                }
                openSubscribe(source, second_source, vipParams);
                !hideToast && handleToast();
                return true;
            }

            // success=false， type=1 提示今天限免资格没了; type=5 提示团队版美豆限额
            if (
                (!infoRef.current?.success && infoRef.current?.type === 1) ||
                (!infoRef.current?.success && infoRef.current?.type === 5)
            ) {
                onErrorMessage && onErrorMessage(infoRef.current.message);
                return true;
            }

            return false;
        };

        handleGenRef.current = async () => {
            if (loading) return;

            if (onBeforeGen) {
                const checkBeforeGen = await onBeforeGen();
                if (!checkBeforeGen) {
                    // 如果返回false，则不进行后续逻辑
                    return;
                }
            }
            // 使用v2的话会在onBeforeGen 算完权益，直接执行
            if (info?.use_v2 || useV2) {
                // hack这个算价会有混合的，返回use_v2=true的算价结果，和没有use_v2字段的算价结果，所以这里强制校验一下，如果是use_v2=true的算价结果才走这个逻辑。咨询@祥帅
                if (forceCheckSuccess && handleSuccessCheckFailed()) {
                    return;
                }
                onGen?.();
                return;
            }

            if (isFree) {
                onGen && onGen();
                return;
            }

            if (handleSuccessCheckFailed()) {
                return;
            }

            // 企业版，不需要二次确认弹窗
            if (currentIdentity?.type !== IdentityType.ENTERPRISE) {
                await checkConfirmStatus(infoRef.current?.price);
            }

            onGen && onGen();
        };

        // useRef解决组件重新渲染后debounce防抖失效问题
        const debounceHandleGen = useCallback(
            debounce(() => handleGenRef.current(), 1000, {
                leading: true,
                trailing: false,
            }),
            [info],
        );

        const handleUpdateInfo: any = (params: { id?: string; data: PriceCalculateResponse['data'] }) => {
            const { id, data } = params;

            if (uuid === id) {
                infoRef.current = data;
                setInfo(data);
                infoCache = data;
            }
        };

        useImperativeHandle(ref, () => ({
            onClick: debounceHandleGen,
        }));

        useEffect(() => {
            eventBus.addListener('xdesign-widgets__meidouInfo__calc-price', handleUpdateInfo);
            return () => {
                eventBus.removeListener('xdesign-widgets__meidouInfo__calc-price', handleUpdateInfo);

                debounceHandleGen.cancel();
            };
        }, []);

        return (
            <>
                <button
                    className={classnames('xdesign-meidou-info-btn', className)}
                    onClick={debounceHandleGen}
                    style={{ ...style }}
                    disabled={disabled || loading}
                    type="button"
                >
                    {loading ? (
                        <IconFont
                            name="LoadingCircleBold"
                            fontSize={16}
                            className="xdesign-meidou-info-btn__loading"
                        ></IconFont>
                    ) : (
                        prefixIconName && (
                            <IconFont name={prefixIconName} fontSize={16} className="xdesign-meidou-info-btn__prefix" />
                        )
                    )}

                    <div>{text}</div>
                    {/* 企业版隐藏美豆算价展示 */}
                    {currentIdentity?.type !== IdentityType.ENTERPRISE && !hideDesc && info && !isFree ? (
                        <>
                            {info?.type === 1 ? (
                                <></>
                            ) : // <div className="xdesign-meidou-info-btn__desc">
                            //   （
                            //   {$t((v, { r }) =>
                            //     r(v.meidouInfo.tryTimes, [info.left_num, info.free_num]),
                            //   )}
                            //   ）packages/applications/designer-editor/src/utils/login.ts
                            // </div>
                            info.price && !hidePrice ? (
                                <span className="xdesign-meidou-info-btn__desc">
                                    {!isIntl && (
                                        <svg
                                            style={{ marginLeft: 4, marginRight: 2 }}
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="7"
                                            height="9"
                                            viewBox="0 0 7 9"
                                            fill="none"
                                        >
                                            <path
                                                d="M5.28554 2.21213C5.38201 2.47537 5.47265 2.7227 5.57524 2.92718C5.68533 3.14662 5.83612 3.3833 5.99857 3.63827C6.57063 4.53613 7.28727 5.66093 6.88059 7.05967C6.35216 8.87717 3.77326 9.78543 1.82233 8.13584C-0.366844 6.2848 -0.232876 3.84692 0.422042 2.20474C0.910089 0.980984 1.76748 0 2.99131 0C4.47485 0 4.92167 1.21924 5.28554 2.21213ZM4.5088 2.64305C4.41951 2.44177 4.20387 2.48661 4.22283 2.70842C4.30239 3.63905 4.63983 4.30139 5.27762 4.75345C5.46567 4.88674 5.62316 4.74528 5.46758 4.50141C5.22874 4.12704 4.75127 3.18964 4.5088 2.64305Z"
                                                fill="#FAB587"
                                            />
                                        </svg>
                                    )}
                                    {/* NOTE: 海外如果有限免的情况不显示美豆数 */}
                                    {isIntl
                                        ? info?.price
                                            ? `（${info.price} ${$t((v) => v.userInfo.credit)}）`
                                            : ``
                                        : info.price}
                                </span>
                            ) : null}
                        </>
                    ) : null}

                    {tooltipTitle || (isIntl && info?.consume_tip) ? (
                        <div className="xdesign-meidou-info-btn__tooltip">
                            <div className="xdesign-meidou-info-btn__tooltip-icon">
                                <IconFont name="QuestionMarkCircle" fontSize={16}></IconFont>
                            </div>

                            <div className="xdesign-meidou-info-btn__tooltip-content">{info?.consume_tip}</div>
                        </div>
                    ) : null}
                    {extra}
                </button>
            </>
        );
    },
);
export default GenButton;
